// client/pages/Reward.tsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Wallet, Gift, X, CheckCircle, AlertCircle } from "lucide-react";
import { toast } from "@/hooks/use-toast";

const Reward = () => {
  const navigate = useNavigate();
  const [walletAddress, setWalletAddress] = useState("");
  const [loading, setLoading] = useState(false);
  const [eligibility, setEligibility] = useState<any>(null);
  const [showDialog, setShowDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [dialogType, setDialogType] = useState<"pending" | "approved" | "received" | "error">("pending");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [userEmail, setUserEmail] = useState("");
  const [userWallet, setUserWallet] = useState("");

  const REQUIRED_SOL_FEE = 0.005;

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const res = await fetch("/api/auth", { method: "POST", credentials: "include" });
      const data = await res.json();
      if (data.success && data.user) {
        setIsAuthenticated(true);
        setUserEmail(data.user.email);
        setUserWallet(data.user.wallets?.solana || data.user.walletAddress || "");
      } else {
        setIsAuthenticated(false);
      }
    } catch (error) {
      setIsAuthenticated(false);
    } finally {
      setCheckingAuth(false);
    }
  };

  const validateWallet = (address: string) => {
    return /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(address);
  };

  const checkEligibility = async () => {
    if (!walletAddress.trim()) {
      toast({ title: "ERROR", description: "ENTER WALLET ADDRESS", variant: "destructive" });
      return;
    }
    if (!validateWallet(walletAddress)) {
      toast({ title: "ERROR", description: "INVALID SOLANA ADDRESS", variant: "destructive" });
      return;
    }

    if (userWallet && userWallet !== walletAddress) {
      setEligibility({ 
        eligible: false, 
        message: `❌ WALLET MISMATCH\n\nYOUR LINKED WALLET: ${userWallet.slice(0, 8)}...${userWallet.slice(-6)}\n\nPLEASE USE YOUR CONNECTED WALLET ADDRESS.` 
      });
      return;
    }

    setLoading(true);
    try {
      const res = await fetch("/api/reward/check-eligibility", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ walletAddress }),
      });
      const data = await res.json();

      if (data.success) {
        const hasSol = data.solBalance >= REQUIRED_SOL_FEE;
        setEligibility({
          eligible: hasSol,
          solBalance: data.solBalance,
          fixercoinBalance: data.fixercoinBalance || 0,
          fxmBalance: data.fxmBalance || 0,
          userEmail: data.userEmail || userEmail,
          requiredFee: REQUIRED_SOL_FEE,
          message: hasSol 
            ? `✅ ELIGIBLE\n\nUSER: ${data.userEmail || userEmail}\nSOL: ${data.solBalance.toFixed(6)} SOL\nFIXERCOIN: ${(data.fixercoinBalance || 0).toFixed(6)}\nFXM: ${(data.fxmBalance || 0).toFixed(6)}`
            : `❌ NEED ${REQUIRED_SOL_FEE} SOL\n\nUSER: ${data.userEmail || userEmail}\nHAVE: ${data.solBalance.toFixed(6)} SOL`
        });
      } else {
        setEligibility({ eligible: false, message: data.message });
      }
    } catch (error) {
      setEligibility({ eligible: false, message: "NETWORK ERROR" });
    } finally {
      setLoading(false);
    }
  };

  const submitClaim = async () => {
    if (!eligibility?.eligible) {
      toast({ title: "NOT ELIGIBLE", description: "CHECK WALLET FIRST", variant: "destructive" });
      return;
    }

    setLoading(true);
    try {
      const res = await fetch("/api/reward/submit-claim", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ 
          walletAddress, 
          solBalance: eligibility.solBalance,
          fixercoinBalance: eligibility.fixercoinBalance,
          fxmBalance: eligibility.fxmBalance,
          requiredFee: REQUIRED_SOL_FEE 
        }),
      });
      const data = await res.json();

      if (data.success) {
        setDialogType("pending");
        setDialogMessage("✅ REQUEST SUBMITTED!\n\nYOUR CLAIM IS PENDING ADMIN APPROVAL.\n\nYOU WILL BE NOTIFIED ONCE APPROVED.");
        setShowDialog(true);
        setEligibility(null);
        setWalletAddress("");
      } else if (data.message?.includes("ALREADY CLAIMED")) {
        setDialogType("approved");
        setDialogMessage("⚠️ ALREADY CLAIMED\n\nYOU HAVE ALREADY RECEIVED THIS REWARD.\n\nEACH WALLET CAN ONLY CLAIM ONCE.");
        setShowDialog(true);
      } else if (data.message?.includes("PENDING")) {
        setDialogType("pending");
        setDialogMessage("⏳ PENDING REQUEST\n\nYOU ALREADY HAVE A PENDING CLAIM.\n\nPLEASE WAIT FOR ADMIN APPROVAL.");
        setShowDialog(true);
      } else {
        throw new Error(data.message);
      }
    } catch (error: any) {
      toast({ title: "ERROR", description: error.message || "SUBMISSION FAILED", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  if (checkingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-yellow-500" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background pt-12 px-4">
        <div className="w-full max-w-md mx-auto text-center">
          <Gift className="h-16 w-16 text-yellow-500 mx-auto mb-6" />
          <h1 className="text-3xl font-bold uppercase text-yellow-500 mb-4">LOGIN REQUIRED</h1>
          <p className="text-muted-foreground mb-8">PLEASE LOGIN TO CLAIM YOUR REWARD</p>
          <Button 
            onClick={() => navigate("/login")} 
            className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold px-8 py-6 text-base"
          >
            LOGIN TO CLAIM
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-12 px-4">
      <div className="w-full max-w-md mx-auto">
        {/* Header - No separator line */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold uppercase text-yellow-500">FIXORIUM REWARD</h1>
          <p className="text-xs text-muted-foreground mt-2">LOGGED IN AS: {userEmail}</p>
          {userWallet && (
            <p className="text-[8px] text-muted-foreground mt-1 font-mono">
              WALLET: {userWallet.slice(0, 8)}...{userWallet.slice(-6)}
            </p>
          )}
        </div>

        {/* Form Area */}
        <div className="space-y-4">
          {/* Info Box - No separator */}
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <Gift className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-[10px] font-semibold text-yellow-500 uppercase">REWARD DETAILS</p>
                <p className="text-[9px] text-muted-foreground mt-0.5">
                  REQUIRE {REQUIRED_SOL_FEE} SOL • REWARD: 1000 FIXERCOIN + 900 FXM
                </p>
              </div>
            </div>
          </div>

          {/* Wallet Input */}
          <div>
            <label className="text-[10px] font-semibold uppercase text-muted-foreground block mb-1.5">
              SOLANA WALLET ADDRESS
            </label>
            <Input
              type="text"
              placeholder="ENTER YOUR SOLANA WALLET ADDRESS"
              value={walletAddress}
              onChange={(e) => {
                setWalletAddress(e.target.value);
                setEligibility(null);
              }}
              className="bg-background border-border text-foreground font-mono text-xs uppercase h-10"
              disabled={loading}
            />
          </div>

          {/* Check Button */}
          <Button
            onClick={checkEligibility}
            disabled={loading || !walletAddress.trim()}
            className="w-full bg-muted hover:bg-muted/80 text-yellow-500 border border-yellow-500/30 h-10 text-xs"
          >
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wallet className="h-3.5 w-3.5 mr-2" />}
            CHECK ELIGIBILITY
          </Button>

          {/* Eligibility Result */}
          {eligibility && (
            <div className={`p-3 rounded-lg text-center text-xs ${
              eligibility.eligible 
                ? "bg-green-950/50 border border-green-500/50 text-green-400"
                : "bg-red-950/50 border border-red-500/50 text-red-400"
            }`}>
              <div className="whitespace-pre-line">{eligibility.message}</div>
              {eligibility.solBalance !== undefined && !eligibility.message?.includes("WALLET MISMATCH") && (
                <div className="text-[9px] mt-1.5 text-muted-foreground space-y-0.5">
                  <p>SOL BALANCE: {eligibility.solBalance.toFixed(6)} SOL</p>
                  <p>FIXERCOIN BALANCE: {(eligibility.fixercoinBalance || 0).toFixed(6)}</p>
                  <p>FXM BALANCE: {(eligibility.fxmBalance || 0).toFixed(6)}</p>
                </div>
              )}
            </div>
          )}

          {/* Submit Button */}
          <Button
            onClick={submitClaim}
            disabled={loading || !eligibility?.eligible}
            className="w-full bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600 text-black font-bold uppercase h-10 text-xs"
          >
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Gift className="h-3.5 w-3.5 mr-2" />}
            CLAIM REWARD
          </Button>

          {/* Footer - No separator */}
          <div className="text-center text-[8px] text-muted-foreground pt-2">
            <p>ONLY STABLE DEX TOKENS ARE LISTED FOR TRADING</p>
          </div>
        </div>
      </div>

      {/* Full Screen Dialog */}
      {showDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
          <div className="w-full max-w-sm mx-4 bg-background rounded-xl p-6">
            <div className="text-center">
              <h2 className="text-xl uppercase text-yellow-500 font-bold mb-3">
                {dialogType === "pending" && "⏳ REQUEST PENDING"}
                {dialogType === "approved" && "✅ ALREADY CLAIMED"}
                {dialogType === "received" && "🎁 REWARD RECEIVED"}
                {dialogType === "error" && "❌ ERROR"}
              </h2>
              
              <div className="whitespace-pre-line text-sm text-foreground mb-4">
                {dialogMessage}
              </div>
              
              {dialogType === "pending" && (
                <div className="bg-yellow-950/50 p-2.5 rounded-lg mb-4 border border-yellow-500/30">
                  <p className="text-[10px] text-yellow-400">CLAIM ID SAVED IN SYSTEM</p>
                </div>
              )}
              {dialogType === "approved" && (
                <div className="bg-green-950/50 p-2.5 rounded-lg mb-4 border border-green-500/30">
                  <p className="text-[10px] text-green-400">TOKENS ALREADY SENT TO YOUR WALLET</p>
                </div>
              )}
              
              <Button 
                onClick={() => setShowDialog(false)} 
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-bold uppercase h-10 text-xs"
              >
                CLOSE
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reward;
